import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface TodoItem {
  id: string;
  description: string;
  author: string;
  status: string;
  priority: string;
  due_date: string | null;
  completed_at: string | null;
  archived_at: string | null;
  tags: string[] | null;
  scheduled_event_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TodoStats {
  open: number;
  in_progress: number;
  done: number;
  overdue: number;
  archived: number;
}

export interface TodoFilters {
  status?: string;
  priority?: string;
  includeArchived?: boolean;
}

export function useTodoStats() {
  return useQuery<TodoStats>({
    queryKey: ["todo-stats"],
    queryFn: () => apiFetch<TodoStats>("/api/dev/todos/stats"),
    refetchInterval: 30_000,
  });
}

export function useTodos(filters: TodoFilters) {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== "all") params.set("status", filters.status);
  if (filters.priority && filters.priority !== "all") params.set("priority", filters.priority);
  if (filters.includeArchived) params.set("include_archived", "true");
  params.set("limit", "100");

  return useQuery<{ items: TodoItem[]; nextCursor: string | null }>({
    queryKey: ["todos", filters],
    queryFn: () =>
      apiFetch<{ items: TodoItem[]; nextCursor: string | null }>(
        `/api/dev/todos?${params.toString()}`
      ),
    refetchInterval: 30_000,
  });
}

export function useUpdateTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, unknown> }) =>
      apiFetch<{ ok: boolean }>(`/api/dev/todos/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
      queryClient.invalidateQueries({ queryKey: ["todo-stats"] });
    },
  });
}

export function useArchiveTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/dev/todos/${id}/archive`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
      queryClient.invalidateQueries({ queryKey: ["todo-stats"] });
    },
  });
}

export function useUnarchiveTodo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/dev/todos/${id}/unarchive`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
      queryClient.invalidateQueries({ queryKey: ["todo-stats"] });
    },
  });
}
